
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';

import { ListStatus, RootActions } from '@/store/types';
import Region from '@/store/models/region';

import Autocomplete, { AutocompleteOption } from '@/components/controls/autocomplete.vue';
import Checkbox from '@/components/controls/checkbox.vue';

export default Vue.extend({
  inject: {
    $validator: '$validator',
  },
  name: 'RegionsSelection',
  components: {
    Checkbox,
    Autocomplete,
  },
  $_veeValidate: {
    name() {
      return this.name || 'regions';
    },
    value() {
      if (this.isAllRegionsSelected && this.hasAllRegionsCheckbox) {
        return this.regions;
      }

      return this.value;
    },
  },
  props: {
    name: {
      type: String,
      default: 'regions',
    },
    value: {
      type: Array as () => Region[],
      default: () => [] as Region[],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasAllRegionsCheckbox: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      regionInput: '',
      isAllRegionsSelected: false,
      localCopyOfRegions: [],
    };
  },
  computed: {
    ...mapState(['regions', 'regionsStatus']),
    regionsOptions(): AutocompleteOption[] {
      const regionsIds = this.value.map(({ id }) => id);

      return this.regions
        .filter((region) => {
          return regionsIds.indexOf(region.id) === -1;
        })
        .map((region) => ({
          label: region.name,
          value: region.id,
        }))
        .filter((option) => option.label.toLowerCase().indexOf(this.regionInput.toLowerCase()) > -1);
    },
    areRegionsLoaded(): boolean {
      return this.regionsStatus === ListStatus.Loaded;
    },
    hasError() {
      return this.errors.has(this.name);
    },
    fieldError() {
      return this.errors.items.find((item) => item.field === this.name);
    },
    fieldErrorRule(): string {
      if (!this.fieldError || !this.fieldError.rule) {
        return '';
      } else {
        return this.fieldError.rule;
      }
    },
    fieldErrorMsg(): string {
      if (!this.fieldError || !this.fieldError.msg) {
        return '';
      } else {
        if (this.label) {
          return this.fieldError.msg.replace(this.name, this.label);
        }

        return this.fieldError.msg;
      }
    },
    showFieldError(): boolean {
      return Boolean(this.fieldErrorMsg.length);
    },
  },
  methods: {
    ...mapActions({
      loadRegions: RootActions.LOAD_REGIONS,
    }),
    addRegion(option: AutocompleteOption) {
      const region = this.regions.find(({ id }) => id === option.value);

      if (region) {
        this.$emit('input', [
          ...this.value,
          Region.fromJSON({
            id: region.id,
            code: region.code,
            name: region.name,
          }),
        ]);
      }
    },
    removeRegion(regionId: string | number) {
      this.$emit(
        'input',
        [...this.value].filter(({ id }) => id !== regionId),
      );
    },
    updateIsAllRegionsSelected(value: boolean) {
      if (!this.hasAllRegionsCheckbox) {
        return;
      }

      if (value) {
        this.isAllRegionsSelected = true;
        this.localCopyOfRegions = [...this.value];
        this.$emit('input', []);
      } else {
        if (this.localCopyOfRegions.length) {
          this.$emit('input', [...this.localCopyOfRegions]);
        }

        this.isAllRegionsSelected = false;
      }
    },
  },
  watch: {
    disabled: {
      handler(value) {
        if (!value) {
          // Reset the local copy of regions
          this.localCopyOfRegions = [];

          if (!this.value.length && this.hasAllRegionsCheckbox) {
            this.isAllRegionsSelected = true;
          }
        }
      },
      immediate: true,
    },
  },
  created() {
    this.loadRegions();
  },
});
